import { Controller } from "@hotwired/stimulus"

// Boards::ShowComponent — mobile Stage Group navigation.
//
// Drives the mobile-only tab row, the visible column at base (<md),
// the swipe gesture on the card area, and the `?sg=<slug>` URL state
// that keeps deep-links and browser back/forward in sync with the
// active Stage Group.
//
// Mobile (<md) writes `?sg=` on every tab change / swipe so the URL
// is shareable and back/forward navigates between Stage Groups.
// Desktop (md+) is read-only: on connect, if `?sg=` is present, the
// matching column is scrolled into view inside the existing
// horizontally-scrollable columns container — mirrors how
// `?parent=KEY-N` works as a server-rendered filter. Desktop never
// writes `?sg=` (the columns are already side-by-side; the param
// would have no UX value, and writing it would surprise users by
// changing the URL on a passive scroll).
//
// The `data-active` attribute on each `.card-column` is the truth.
// CSS in `column_component/styles.css` hides inactive columns at base
// and is dormant at `md+`. The server stamps an initial value on first
// paint so users on slow mobile do not flash all columns before the
// controller mounts.
export default class extends Controller {
  static targets = ["tabs", "columns"]
  static values = { defaultSlug: String }

  // Pointer-based swipe threshold (CSS px). Tuned to be larger than a
  // typical tap drift; smaller than a casual horizontal pan so the
  // gesture isn't triggered when the user is just scrolling vertically.
  static SWIPE_THRESHOLD = 50

  // Vertical-to-horizontal ratio above which we treat the gesture as a
  // scroll, not a swipe. Without this, a finger drag down-and-slightly-
  // right would steal the swipe handler from the page's scroll.
  static VERTICAL_TOLERANCE = 0.7

  connect() {
    this.mql = window.matchMedia("(min-width: 768px)")
    this.mqlHandler = () => this.applyState()
    this.mql.addEventListener("change", this.mqlHandler)

    this.popHandler = () => this.applyState()
    window.addEventListener("popstate", this.popHandler)

    this.applyState()
  }

  disconnect() {
    if (this.mql) this.mql.removeEventListener("change", this.mqlHandler)
    window.removeEventListener("popstate", this.popHandler)
  }

  // Tab click handler — wired by `data-action="click->...#select"` on
  // each tab in the template. Writes `?sg=` (mobile only) and updates
  // the rendered active state.
  select(event) {
    const slug = event.currentTarget?.dataset?.slug
    if (!slug) return
    this.activate(slug, { writeUrl: !this.isDesktop() })
  }

  touchStart(event) {
    if (this.isDesktop()) return
    const touch = event.changedTouches?.[0]
    if (!touch) return
    this.swipeStart = { x: touch.clientX, y: touch.clientY }
  }

  touchEnd(event) {
    if (this.isDesktop() || !this.swipeStart) return
    const touch = event.changedTouches?.[0]
    if (!touch) return
    const dx = touch.clientX - this.swipeStart.x
    const dy = touch.clientY - this.swipeStart.y
    this.swipeStart = null
    if (Math.abs(dx) < this.constructor.SWIPE_THRESHOLD) return
    if (Math.abs(dy) / Math.abs(dx) > this.constructor.VERTICAL_TOLERANCE) {
      return
    }
    const next = this.adjacentSlug(dx < 0 ? 1 : -1)
    if (next) this.activate(next, { writeUrl: true })
  }

  applyState() {
    const slug = this.resolveActiveSlug()
    if (!slug) return
    this.renderActive(slug)
    // MC-155: desktop is read-only on `?sg=`. Only scroll a column
    // into view when the slug was explicitly named in the URL — the
    // fallback path (no `?sg=`, slug came from `defaultSlugValue` /
    // first column) must produce no scroll, per MC-136's acceptance
    // criterion.
    if (this.isDesktop() && this.currentSlugInUrl()) this.scrollIntoView(slug)
  }

  activate(slug, { writeUrl }) {
    this.renderActive(slug)
    if (writeUrl) this.writeUrl(slug)
  }

  renderActive(slug) {
    for (const tab of this.tabButtons()) {
      const match = tab.dataset.slug === slug
      tab.setAttribute("aria-selected", match ? "true" : "false")
      if (match) this.ensureTabInView(tab)
    }
    for (const column of this.columnElements()) {
      const match = column.dataset.stageGroupSlug === slug
      column.dataset.active = match ? "true" : "false"
    }
  }

  // Mobile only: drop `?sg=` when the active slug is the default (the
  // first Stage Group) so a clean board URL stays clean.
  writeUrl(slug) {
    if (!window.history?.replaceState) return
    const url = new URL(window.location.href)
    if (slug === this.defaultSlugValue) {
      url.searchParams.delete("sg")
    } else {
      url.searchParams.set("sg", slug)
    }
    const baseState = window.history.state || {}
    if (slug === this.currentSlugInUrl()) {
      window.history.replaceState(baseState, "", url)
    } else {
      window.history.pushState(baseState, "", url)
    }
  }

  resolveActiveSlug() {
    const fromUrl = this.currentSlugInUrl()
    if (fromUrl && this.slugExists(fromUrl)) return fromUrl
    return this.defaultSlugValue || this.columnElements()[0]?.dataset.stageGroupSlug
  }

  currentSlugInUrl() {
    const params = new URLSearchParams(window.location.search)
    return params.get("sg")
  }

  slugExists(slug) {
    return this.columnElements().some(
      (column) => column.dataset.stageGroupSlug === slug,
    )
  }

  adjacentSlug(direction) {
    const slugs = this.columnElements().map((c) => c.dataset.stageGroupSlug)
    const currentIndex = slugs.indexOf(this.resolveActiveSlug())
    if (currentIndex === -1) return null
    const nextIndex = currentIndex + direction
    if (nextIndex < 0 || nextIndex >= slugs.length) return null
    return slugs[nextIndex]
  }

  scrollIntoView(slug) {
    const column = this.columnElements().find(
      (c) => c.dataset.stageGroupSlug === slug,
    )
    if (!column || !this.hasColumnsTarget) return
    // `scrollIntoView({ inline: "start" })` scrolls the nearest
    // scrollable ancestor — the `card-board__columns` container — so
    // the column lines up at the left edge. The smooth behaviour
    // matches the existing PDE-824 horizontal-scroll behaviour for
    // the drawer-open path.
    column.scrollIntoView({ behavior: "smooth", inline: "start", block: "nearest" })
  }

  ensureTabInView(tab) {
    if (!this.hasTabsTarget) return
    if (typeof tab.scrollIntoView !== "function") return
    tab.scrollIntoView({ behavior: "smooth", inline: "nearest", block: "nearest" })
  }

  isDesktop() {
    return this.mql ? this.mql.matches : false
  }

  tabButtons() {
    if (!this.hasTabsTarget) return []
    return Array.from(this.tabsTarget.querySelectorAll("[data-slug]"))
  }

  columnElements() {
    if (!this.hasColumnsTarget) return []
    return Array.from(
      this.columnsTarget.querySelectorAll("[data-stage-group-slug]"),
    )
  }
}
